import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../backend';
import authHeader from '../services/auth-header';
import { SubscriptionContext } from './SubscriptionContext';
import { readCache, writeCache, tryAcquireLock, releaseLock } from '../utils/crossTabCache';

const CACHE_KEY = 'athena:subscription:planFeatures';
const LOCK_KEY = 'athena:subscription:planFeatures:lock';
const LOCK_TTL_MS = 5000;
const INITIAL_PAINT_TTL_MS = 5 * 60 * 1000;
export function SubscriptionProvider({ children }) {
  const cached = readCache(CACHE_KEY, INITIAL_PAINT_TTL_MS);
  const [planFeatures, setPlanFeatures] = useState(cached);
  const [loading, setLoading] = useState(cached === null);
  const retryTimeoutRef = useRef(null);

  const fetchSubscriptionFeatures = useCallback(async (options = {}) => {
    const { force = false } = options;

    if (!force) {
      if (!tryAcquireLock(LOCK_KEY, LOCK_TTL_MS)) {
        retryTimeoutRef.current = setTimeout(() => {
          const retryCached = readCache(CACHE_KEY, LOCK_TTL_MS + 250);
          if (retryCached !== null) {
            setPlanFeatures(retryCached);
            setLoading(false);
          } else {
            fetchSubscriptionFeatures();
          }
        }, LOCK_TTL_MS + 250);
        return;
      }
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}paymentsub/subscription/`, {
        headers: authHeader(),
      });

      if (response.data.success === 1 && response.data.data) {
        const subscriptionData = response.data.data;
        // Missing plan object (or a request error, below) is treated as
        // "LEGACY" — all features enabled — matching the fallback several
        // pages already relied on to avoid locking users out.
        const value = subscriptionData.plan || 'LEGACY';
        setPlanFeatures(value);
        writeCache(CACHE_KEY, value);
      }
    } catch (error) {
      console.error('Error fetching subscription features:', error);
      setPlanFeatures('LEGACY');
      writeCache(CACHE_KEY, 'LEGACY');
    } finally {
      setLoading(false);
      releaseLock(LOCK_KEY);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionFeatures();

    const handleStorage = (e) => {
      if (e.key === CACHE_KEY) {
        const updated = readCache(CACHE_KEY, LOCK_TTL_MS + 250);
        if (updated !== null) {
          setPlanFeatures(updated);
          setLoading(false);
        }
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearTimeout(retryTimeoutRef.current);
    };
  }, [fetchSubscriptionFeatures]);

  const value = useMemo(
    () => ({
      planFeatures,
      loading,
      refreshSubscriptionFeatures: () => fetchSubscriptionFeatures({ force: true }),
    }),
    [planFeatures, loading, fetchSubscriptionFeatures],
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
